import { useEffect, useRef, useState } from "react";
import { Upload, Download, CheckCircle2, XCircle, PlugZap, FlaskConical, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { api, errMsg, has } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/app/PageHeader";

const Card = ({ children, testId, className = "" }) => <div className={`rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6 ${className}`} data-testid={testId}>{children}</div>;

const download = async (path, name) => {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const Report = ({ r }) => (
  <Card testId="import-report">
    <p className="font-semibold text-stone-800">{r.status === "committed" ? "Import completed" : "Validation report (nothing saved yet)"} — {r.filename}</p>
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
      {[["Rows read", r.total_rows], ["New", r.inserted], ["Updated", r.updated], ["Unchanged (duplicates)", r.unchanged], ["Rejected", r.rejected]].map(([l, v]) => (
        <div key={l} className="rounded-lg bg-[#F5F0E6] p-3" data-testid={`report-${l.split(" ")[0].toLowerCase()}`}><p className="font-mono-data text-2xl text-amber-950">{v}</p><p className="text-xs text-stone-600">{l}</p></div>
      ))}
    </div>
    {r.ignored_columns?.length > 0 && <p className="mt-4 text-xs text-stone-600">Ignored columns (not stored, for privacy or not recognised): {r.ignored_columns.join(", ")}</p>}
    <p className="mt-2 text-xs text-stone-600">Reconciliation — valid rows per GKK: {Object.entries(r.reconciliation.households_per_gkk).map(([k, v]) => `${k}: ${v}`).join(" · ") || "none"}; people in valid rows: {r.reconciliation.individuals_in_valid_rows}</p>
    {r.errors?.length > 0 && (
      <div className="mt-4 max-h-64 overflow-auto rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900" data-testid="import-errors">
        {r.errors.map((e) => <p key={e.row}>Row {e.row}: {e.errors.join("; ")}</p>)}
      </div>
    )}
  </Card>
);

const SampleCard = ({ onChange }) => {
  const [active, setActive] = useState(false);
  const load = () => api.get("/admin/sample-status").then((r) => setActive(r.data.active));
  useEffect(() => { load(); }, []);
  const act = async (method) => {
    try { await api[method]("/admin/sample-data"); toast.success(method === "delete" ? "Sample data removed" : "Sample data loaded"); load(); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testId="sample-data-card">
      <p className="flex items-center gap-2 font-semibold text-stone-800"><FlaskConical className="h-4 w-4" /> TEST sample data</p>
      <p className="mt-2 text-sm text-stone-600">{active ? "Sample GKKs (named “TEST GKK …”) and households are loaded so you can try the system. Remove them before using real parish data." : "No sample data is loaded."}</p>
      {active ? (
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" className="mt-4" data-testid="remove-sample-button">Remove all sample data</Button></AlertDialogTrigger>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader><AlertDialogTitle>Remove sample data?</AlertDialogTitle><AlertDialogDescription>Only records labeled TEST are removed. Real imported records are not touched.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => act("delete")} data-testid="confirm-remove-sample">Remove</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : <Button variant="outline" className="mt-4" onClick={() => act("post")} data-testid="load-sample-button">Load sample data</Button>}
    </Card>
  );
};

export default function ImportData() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);
  const loadBatches = () => api.get("/import/batches").then((r) => setBatches(r.data));
  useEffect(() => { loadBatches(); }, []);

  const send = async (commit) => {
    if (!file) return toast.error("Choose a CSV or Excel file first");
    const fd = new FormData();
    fd.append("file", file); fd.append("commit", commit ? "true" : "false");
    setBusy(true);
    try { const r = await api.post("/import/upload", fd); setReport(r.data); if (commit) toast.success("Import saved"); loadBatches(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Data management" title="Survey Import" testId="import-header">
        Upload an export from the Parish Online Survey. Every file is checked first; nothing is saved until you confirm. Rows without recorded consent are rejected. Names and addresses are never stored.
      </PageHeader>
      <Card testId="integration-status-card" className="bg-[#F5F0E6]">
        <p className="flex items-center gap-2 font-semibold text-stone-800"><PlugZap className="h-4 w-4" /> Direct connection to Parish Online Survey: not connected</p>
        <p className="mt-2 text-sm text-stone-600">A live connection needs the survey system's API details and written administrator authorization. Until then, use the validated file import below — it is the safe alternative and avoids copying the whole private database.</p>
      </Card>
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card testId="upload-card">
          <p className="font-semibold text-stone-800">1. Download the template · 2. Fill it in · 3. Check · 4. Save</p>
          <p className="mt-2 text-xs text-stone-500">GKK and Unit names must match the GKK & Unit List exactly. Multiple answers are separated with “;”. The same household_ref + survey_period is updated, never duplicated.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => download("/import/template", "mabuhay-survey-template.csv")} data-testid="download-template-button"><Download className="h-4 w-4 mr-2" />Template</Button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setReport(null); }} data-testid="survey-import-file-input" />
            <Button variant="outline" onClick={() => fileRef.current.click()} data-testid="survey-import-upload-button"><Upload className="h-4 w-4 mr-2" />{file ? file.name : "Choose file"}</Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button disabled={busy || !file} onClick={() => send(false)} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="validate-import-button"><CheckCircle2 className="h-4 w-4 mr-2" />Check file</Button>
            <Button disabled={busy || !report || report.status !== "validated" || report.valid_rows === 0} onClick={() => send(true)} className="bg-[#285E3B] hover:bg-[#1E462C]" data-testid="commit-import-button">Save {report?.valid_rows ?? ""} valid {report?.valid_rows === 1 ? "row" : "rows"}</Button>
          </div>
        </Card>
        <div className="space-y-6">
          {has(user, "sample:manage") && <SampleCard onChange={loadBatches} />}
          {has(user, "backup:export") && (
            <Card testId="backup-card">
              <p className="flex items-center gap-2 font-semibold text-stone-800"><DatabaseBackup className="h-4 w-4" /> Backup</p>
              <p className="mt-2 text-sm text-stone-600">Download a full copy of the master list, survey records and sync log. Store it securely.</p>
              <Button variant="outline" className="mt-4" onClick={() => download("/admin/backup", "mabuhay-backup.json")} data-testid="backup-download-button">Download backup</Button>
            </Card>
          )}
        </div>
      </div>
      {report && <Report r={report} />}
      <Card testId="sync-log">
        <p className="font-semibold text-stone-800">Synchronization log</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-stone-500"><th className="py-2">When</th><th>File / source</th><th>Status</th><th>By</th><th>New</th><th>Updated</th><th>Rejected</th></tr></thead>
            <tbody>{batches.filter((b) => b.status === "committed").map((b) => (
              <tr key={b.batch_id} className="border-t border-amber-700/10">
                <td className="py-2 whitespace-nowrap">{new Date(b.at).toLocaleString()}</td><td>{b.filename}</td>
                <td>{b.status === "committed" ? <span className="text-[#285E3B]">saved</span> : <span className="text-stone-500">checked only</span>}</td>
                <td>{b.uploaded_by}</td><td className="font-mono-data">{b.inserted}</td><td className="font-mono-data">{b.updated}</td>
                <td className="font-mono-data">{b.rejected ? <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="h-3 w-3" />{b.rejected}</span> : 0}</td>
              </tr>))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
